import { ApiError, errorKeyForSuiteApiResult } from './errors';
import { PortalClient } from './portal-client';
import type { RegisterAccountRequest } from './types';
import { ESuiteApiResult } from './types';

/**
 * Registration API — thin, typed wrapper over the Portal's self-registration
 * endpoint:
 *
 *   PUT SuiteAPI/User/SelfRegisterV1
 *   Headers: brandID (same as Login/Logout — see PortalClient.buildHeaders).
 *     No userName/userPWD: the account does not exist yet, so this call is
 *     unauthenticated.
 *   Body (RegisterAccountRequest): { title?: string; firstName: string;
 *     lastName: string; email: string; newPassword: string }
 *   Response: SuiteApiResult<boolean> — `data` is true on success; the account
 *     still has to be activated via the confirmation e-mail.
 *
 * See Server/Portal/Areas/SuiteAPI/Controllers/UserController.cs for the
 * Portal-side implementation and
 * Server/Portal/Areas/SuiteAPI/RequestModels/SelfRegisterRequest.cs for the DTO.
 */
export class RegistrationApi {
  private readonly client: PortalClient;

  public constructor(client: PortalClient) {
    this.client = client;
  }

  /**
   * Creates a new (not yet activated) Portal account.
   *
   * @throws {ApiError} carrying a translatable key on failure.
   */
  public async register(request: RegisterAccountRequest, signal?: AbortSignal): Promise<void> {
    const result = await this.client.request<boolean>({
      method: 'PUT',
      path: '/SuiteAPI/User/SelfRegisterV1',
      body: request,
      ...(signal ? { signal } : {}),
    });

    // `serverResult` is the classic EResult on PortalClient's public contract, but this
    // endpoint's wire format actually uses the numerically different SuiteAPI EResult
    // (see ESuiteApiResult in ./types). Re-type only this narrow field — not the whole
    // response object — to bridge the two incompatible server contracts.
    const serverResult = result.serverResult as unknown as ESuiteApiResult;

    if (serverResult !== ESuiteApiResult.Success) {
      throw new ApiError('server', errorKeyForSuiteApiResult(serverResult), { serverResult });
    }
  }
}
